import { useEffect, useState } from "react";
import { api } from "../lib/api";

const CELL = {
  green: "bg-emerald-500",
  red: "bg-red-500",
  none: "bg-slate-100",
};

const dayLabel = (d) => {
  const dt = new Date(d + "T00:00:00");
  return dt.toLocaleDateString("en", { weekday: "short" }) + " " + dt.getDate();
};

const taskCell = (pct) => {
  if (pct == null) return "bg-slate-100 text-slate-400";
  if (pct >= 80) return "bg-emerald-500 text-white";
  if (pct >= 50) return "bg-amber-400 text-white";
  return "bg-red-500 text-white";
};

export const Heatmap = ({ departmentId = "", venueId = "" }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    const params = {};
    if (departmentId) params.department_id = departmentId;
    else if (venueId) params.venue_id = venueId;
    api.get("/analytics/heatmap", { params }).then((r) => setData(r.data)).catch(() => {});
  }, [departmentId, venueId]);

  if (!data) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="compliance-heatmap">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl font-bold">Weekly Compliance Heatmap</h2>
        <div className="flex gap-4 text-xs font-semibold text-slate-400">
          <span><span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-500 mr-1.5" />Ready</span>
          <span><span className="inline-block h-2.5 w-2.5 rounded-sm bg-red-500 mr-1.5" />Red</span>
          <span><span className="inline-block h-2.5 w-2.5 rounded-sm bg-slate-100 border border-slate-200 mr-1.5" />No check</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-separate" style={{ borderSpacing: "4px" }}>
          <thead>
            <tr>
              <th className="text-left text-xs font-bold text-slate-400 uppercase tracking-wider pr-4 min-w-32">Staff</th>
              {data.days.map((d) => (
                <th key={d} className="text-xs font-bold text-slate-400 pb-2 min-w-12">{dayLabel(d)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.staff.map((s) => (
              <tr key={s.user_id} data-testid={`heatmap-row-${s.user_id}`}>
                <td className="pr-4 py-1">
                  <p className="text-sm font-bold leading-tight">{s.name}</p>
                  <p className="text-[11px] text-slate-400">{s.position}</p>
                </td>
                {s.cells.map((c) => (
                  <td key={c.date}>
                    <div title={`${s.name} · ${c.date} · ${c.status === "none" ? "no check" : c.status}`}
                      className={`h-9 rounded-md ${CELL[c.status]}`} />
                  </td>
                ))}
              </tr>
            ))}
            <tr data-testid="heatmap-task-row">
              <td className="pr-4 py-1 pt-3">
                <p className="text-sm font-bold">Task completion</p>
                <p className="text-[11px] text-slate-400">% done per day</p>
              </td>
              {data.tasks.map((t) => (
                <td key={t.date} className="pt-2">
                  <div title={`${t.date} · ${t.completed}/${t.total} tasks completed`}
                    className={`h-9 rounded-md flex items-center justify-center text-[11px] font-bold ${taskCell(t.pct)}`}>
                    {t.pct == null ? "—" : `${t.pct}%`}
                  </div>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
